/**
 * 通用侧边面板组件。
 *
 * 该组件提供了一个可复用的侧边滑出面板，支持两种展示方向：
 * - right（右侧滑出）：面板从右侧滑入，占据全高，适用于详情展示
 * - bottom（底部弹起）：面板从底部弹起，占据接近全屏高度，适用于大型内容展示
 *
 * 核心特性：
 * - 命令式 API：通过 `show(title, width?, position?)` 打开，`close()` 关闭
 * - 背景遮罩：半透明遮罩，点击关闭面板
 * - ESC 键关闭：支持多面板堆叠，ESC 只关闭最后打开的面板（分级关闭）
 * - 位置切换动画冻结：切换 right/bottom 位置时冻结过渡动画，避免对角线滑动
 * - 通过 `<slot>` 插槽接收外部内容，父组件可自由填充面板内容
 * - 退出动画：面板关闭时有平滑的滑出/滑下过渡效果
 *
 * 导出：`CrmSidePanel`（自定义元素 `crm-side-panel`）
 *
 * 依赖模块：`BaseElement`（基础元素）
 */
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';

/**
 * 通用侧边面板组件。
 *
 * 继承 `BaseElement`，通过 `@customElement('crm-side-panel')` 注册为自定义元素。
 * 使用静态 Set `_openPanels` 追踪所有已打开的面板实例，实现 ESC 分级关闭。
 * 面板内容通过 `<slot>` 插槽注入，适合展示详情、表单等富内容。
 */
@customElement('crm-side-panel')
export class CrmSidePanel extends BaseElement {
  /** 静态集合：追踪所有当前已打开的面板实例，用于 ESC 分级关闭 */
  private static _openPanels: Set<CrmSidePanel> = new Set();

  /** 面板是否打开 */
  @state()
  private open = false;

  /** 面板标题，显示在头部 */
  @state()
  private panelTitle = '';

  /** 面板宽度（像素），仅 right 位置生效 */
  @state()
  private panelWidth = 480;

  /** 面板展示方向：right=右侧滑出，bottom=底部弹起 */
  @state()
  private position: 'right' | 'bottom' = 'right';

  /** 是否冻结过渡动画（切换位置时临时禁用动画避免对角线滑动） */
  @state()
  private freezeTransition = false;
  /** 标记 mousedown 是否发生在背景遮罩上，用于区分背景点击与面板内容点击 */
  private _backdropMousedown = false;

  static styles = css`
    :host { display: block; }

    /* 背景遮罩：半透明黑色，关闭时淡出并禁用指针事件 */
    .backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.2);
      z-index: var(--crm-panel-z, 50);
      transition: opacity 0.25s ease-out;
    }
    .backdrop--closed { opacity: 0; pointer-events: none; }

    /* 面板容器（右侧模式）：固定定位全高，关闭时向右滑出 */
    .panel {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      max-width: 100vw;
      background: var(--color-surface, #ffffff);
      border-left: 1px solid var(--color-border, #e0e1e6);
      box-shadow: -4px 0 24px rgba(0, 0, 0, 0.08);
      z-index: calc(var(--crm-panel-z, 50) + 1);
      display: flex;
      flex-direction: column;
      transform: translateX(0);
      transition: transform 0.25s ease-out;
    }
    /* 右侧模式关闭态：向右平移 100% 完全隐藏 */
    .panel--closed { transform: translateX(100%); }
    /* 底部模式关闭态：向下平移 100% 完全隐藏 */
    .panel--bottom.panel--closed { transform: translateY(100%); }
    /* 冻结态：禁用所有过渡动画 */
    .freeze { transition: none !important; }

    /* 面板容器（底部模式）：从底部弹起，占据接近全屏高度，圆角顶部 */
    .panel--bottom {
      top: 2vh;
      right: 0;
      bottom: auto;
      left: 0;
      height: 98vh;
      max-width: 100vw;
      border-left: none;
      border-top: 1px solid var(--color-border, #e0e1e6);
      border-radius: 16px 16px 0 0;
      box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.1);
      transform: translateY(0);
      transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }

    /* 面板头部：标题 + 关闭按钮，固定高度不收缩 */
    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid var(--color-border, #e0e1e6);
      flex-shrink: 0;
    }
    .panel-header h2 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--color-text, #1c2024);
    }
    /* 关闭按钮：透明背景，悬停时显示浅色底色 */
    .panel-close {
      padding: 6px;
      border-radius: 8px;
      cursor: pointer;
      color: var(--color-text-muted, #b0b4ba);
      transition: background 0.15s, color 0.15s;
      background: transparent;
      border: none;
      display: flex;
      align-items: center;
    }
    .panel-close:hover {
      background: var(--color-bg, #f0f0f3);
      color: var(--color-text, #1c2024);
    }
    /* 关闭按钮键盘焦点态：蓝色轮廓 */
    .panel-close:focus-visible {
      outline: 2px solid var(--color-focus-blue, #2547d0);
      outline-offset: 2px;
    }

    /* 面板内容区域：可滚动，flex 布局撑满剩余空间 */
    .panel-body {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  `;

  /**
   * 命令式打开面板。
   *
   * 设置标题、宽度（可选）和位置（可选）。若位置发生变更，先冻结过渡动画
   * 避免从旧位置的关闭态直接动画到新位置导致对角线滑动。
   * 使用双重 requestAnimationFrame 确保冻结 class 应用后再解除冻结并打开面板，
   * 保证浏览器在两帧之间完成重排。
   *
   * @param panelTitle - 面板标题文本
   * @param width - 面板宽度（像素），仅 right 位置生效，不传则使用默认值 480
   * @param position - 面板方向，'right' 或 'bottom'，不传则保持当前方向
   */
  show(panelTitle: string, width?: number, position?: 'right' | 'bottom') {
    this.panelTitle = panelTitle;
    if (width) this.panelWidth = width;
    if (position && this.position !== position) {
      // 切换位置时冻结过渡，避免关闭态之间产生对角线动画
      this.freezeTransition = true;
      this.position = position;
    }
    // 双重 rAF：第一帧让浏览器应用冻结 class 和新位置样式，
    // 第二帧解除冻结并打开，确保位置切换无动画闪烁
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.freezeTransition = false;
        this.open = true;
        CrmSidePanel._openPanels.add(this);
      });
    });
  }

  /**
   * 关闭面板。
   *
   * 设置 open 为 false 触发滑出动画，从静态集合中移除当前实例，
   * 并向上派发 `crm-panel-close` 事件通知父组件。
   */
  close() {
    if (!this.open) return;
    this.open = false;
    CrmSidePanel._openPanels.delete(this);
    this.dispatchEvent(new CustomEvent('crm-panel-close', { bubbles: true, composed: true }));
  }

  /**
   * 背景遮罩点击处理器。
   *
   * 仅当 mousedown 起始于背景遮罩本身时才关闭面板，
   * 防止用户在面板内容上拖拽选中文本时误触发关闭。
   */
  private handleBackdropClick() {
    if (this._backdropMousedown) this.close();
  }

  /**
   * 全局 ESC 键处理器。
   *
   * 实现多面板堆叠的分级关闭：ESC 只关闭最后打开的面板。
   * 通过检查 `_openPanels` 集合的最后一个元素判断当前面板是否为最顶层。
   *
   * @param e - 键盘事件对象
   */
  private handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.open) {
      // 只让最后打开的 panel 响应 ESC，实现分级关闭
      const panels = Array.from(CrmSidePanel._openPanels);
      if (panels[panels.length - 1] === this) {
        this.close();
      }
    }
  };

  /** 生命周期：挂载时注册全局 ESC 键监听 */
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this.handleKeydown);
  }

  /** 生命周期：卸载时移除 ESC 键监听并从静态集合中清除当前实例 */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this.handleKeydown);
    CrmSidePanel._openPanels.delete(this);
  }

  /**
   * 主渲染方法。
   *
   * 根据 open 状态和 position 组合面板的 CSS 类名：
   * - 背景遮罩：open 时显示，关闭时添加 backdrop--closed 类淡出
   * - 面板容器：根据位置添加 panel--bottom 类，关闭时添加 panel--closed 类
   * - 冻结态：添加 freeze 类禁用过渡动画
   *
   * 面板内容通过 `<slot>` 插槽接收，ARIA 属性确保无障碍可访问性。
   *
   * @returns Lit 模板结果
   */
  render() {
    // 根据开闭状态和位置组合面板 CSS 类名：open 决定是否添加 panel--closed，position 决定是否添加 panel--bottom
    const panelClass = (this.open ? `panel ${this.position === 'bottom' ? 'panel--bottom' : ''}` : `panel panel--closed ${this.position === 'bottom' ? 'panel--bottom' : ''}`) + (this.freezeTransition ? ' freeze' : '');
    const backdropClass = this.open ? 'backdrop' : 'backdrop backdrop--closed';

    return html`
      <div class="${backdropClass}" aria-hidden="true" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${this.handleBackdropClick}></div>
      <div class="${panelClass}" style="${this.position === 'bottom' ? '' : `width: ${this.panelWidth}px`}" role="dialog" aria-modal="${this.open}" aria-label="${this.panelTitle}" aria-hidden="${!this.open}">
        <div class="panel-header">
          <h2>${this.panelTitle}</h2>
          <button class="panel-close" @click=${this.close} aria-label="关闭">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
        <div class="panel-body" style="flex:1;min-height:0;">
          <slot style="display:flex;flex-direction:column;flex:1;min-height:0;"></slot>
        </div>
      </div>
    `;
  }
}